import { Box, ColumnLayout, Container, Header } from '@amzn/awsui-components-console';
import { useIntl } from 'react-intl';

import type { Csat } from '@/api/generated';

import messages from './index.messages';

interface ClassPerformanceSectionProps {
    csatData: Csat;
    classAttendance: number;
}

/**
 * ClassPerformanceSection component displays class performance metrics
 *
 * @param {Object} props - Component props
 * @param {string} props.overallScore - Overall CSAT score
 * @param {string} props.instructorScore - Instructor score
 * @param {string} props.attendedCounter - Number of students who attended
 * @param {string} props.responseRate - CSAT response rate formatted as percentage
 * @returns {JSX.Element} ClassPerformanceSection component
 */
const ClassPerformanceSection = ({ csatData, classAttendance }: ClassPerformanceSectionProps) => {
    const { formatMessage } = useIntl();
    if (!csatData) return null;

    const formattedCsatResponseRate = classAttendance
        ? `${((csatData.completed_responses / classAttendance) * 100).toFixed(0)}%`
        : '--';
    const classPerformanceData = {
        classOverallCsatLabel: csatData.overall_score || '--',
        classInstructorCsatLabel: csatData.instructor_score || '--',
        classCompletedLabel: csatData.completed_responses || '--',
        classAttendedLabel: classAttendance || '--',
        classResponseRateLabel: formattedCsatResponseRate,
    };

    return (
        <Container
            header={<Header variant='h2'>{formatMessage(messages.classPerformanceLabel)}</Header>}
            data-testid='class-performance-section'
        >
            <ColumnLayout columns={5} borders='vertical' variant='text-grid'>
                {Object.entries(classPerformanceData).map(([label, value]) => (
                    <div key={`${label}-${value}`}>
                        <Box variant='awsui-key-label'>{formatMessage(messages[label])}</Box>
                        <Box
                            variant='strong'
                            fontSize='display-l'
                            color='text-status-info'
                            margin={{ top: 's' }}
                        >
                            {value}
                        </Box>
                    </div>
                ))}
            </ColumnLayout>
        </Container>
    );
};

export default ClassPerformanceSection;
